import {
  useFavoriteSession,
  useGetSessionsByEventId,
  useGetTicketTypesByEventId,
  useUserEventTicket,
} from '@sunrise-ui/api-client';
import {
  Button,
  ButtonVariants,
  TextReg,
  useAuthStore,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { useTicketPurchase } from '../../hooks/useTicketPurchase';
import { TicketReceiptModal } from '../communities/TicketReceiptModal';

type Props = {
  eventId: string;
  size?: 'small' | 'medium';
};

/**
 * EventTicketButton - A ticket purchase/view button for a specific event.
 *
 * Similar to CommunityTicketButton but takes eventId directly instead of
 * deriving it from community sessions. Shows "Get Tickets" when user doesn't
 * have a ticket, and "View My Ticket" when they do.
 *
 * Handles three ticketing paths:
 * 1. External ticketing - opens external URL
 * 2. Internal free ticket - creates ticket via API
 * 3. Internal paid ticket - opens checkout URL with prefilled user data
 */
export function EventTicketButton({ eventId, size = 'small' }: Props) {
  const styles = useDynamicStyles(createStyles);
  const { isAuthenticated } = useAuthStore();
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  // Get sessions for this event (for auto-favoriting single session events)
  const { data: sessions } = useGetSessionsByEventId(eventId || undefined);

  // Get the single session ID for auto-favoriting (only for single session events)
  const singleSessionId = useMemo(() => {
    if (!sessions || sessions.length !== 1) return null;
    return sessions[0]?.id ?? null;
  }, [sessions]);

  const { mutate: favoriteSession } = useFavoriteSession(
    singleSessionId ?? '',
    eventId,
  );

  const {
    data: notOnSaleTicketTypes,
    isLoading: isLoadingNotOnSaleTicketTypes,
  } = useGetTicketTypesByEventId({
    eventId,
    isOnSale: false,
  });

  const { data: onSaleTicketTypes, isLoading: isLoadingOnSaleTicketTypes } =
    useGetTicketTypesByEventId({
      eventId,
      isOnSale: true,
    });

  // Handle ticket purchase
  const { executePurchase, isPurchasing, event, isLoadingPurchase } =
    useTicketPurchase({
      eventId,
      onSuccess: () => {
        if (singleSessionId) {
          favoriteSession();
        }
      },
    });

  const { userTicket, isLoadingTickets } = useUserEventTicket(
    eventId,
    event?.community_id,
  );

  const handleButtonPress = useCallback(async () => {
    // Users with tickets can view receipt (already authenticated)
    if (isAuthenticated && userTicket) {
      setShowReceiptModal(true);
      return;
    }

    // All ticket purchases require authentication
    await executePurchase();
  }, [isAuthenticated, userTicket, executePurchase]);

  const isExternallyTicketed = Boolean(event?.ticketing_platform_url);

  const isLoadingTicketTypes =
    isLoadingNotOnSaleTicketTypes || isLoadingOnSaleTicketTypes;
  const hasAnyTicketTypes = Boolean(
    notOnSaleTicketTypes?.length || onSaleTicketTypes?.length,
  );

  // Don't show button if still loading event or ticket types
  if (isLoadingPurchase || isLoadingTicketTypes || !event || isLoadingTickets) {
    return (
      <Button
        size={size}
        variant={ButtonVariants.PURPLE}
        loading={true}
        disabled={true}
        style={styles.button}
      >
        {''}
      </Button>
    );
  }

  // For authenticated users who have tickets, show "View My Ticket"
  if (isAuthenticated && userTicket) {
    return (
      <>
        <Button
          size={size}
          variant={ButtonVariants.WHITE}
          onPress={handleButtonPress}
          style={styles.button}
        >
          <TextReg>View My Ticket</TextReg>
        </Button>

        <TicketReceiptModal
          visible={showReceiptModal}
          onDismiss={() => setShowReceiptModal(false)}
          ticket={userTicket}
          event={event}
        />
      </>
    );
  }

  // If no ticket types exist at all, show "Tickets coming soon"
  if (!hasAnyTicketTypes && !isExternallyTicketed) {
    return (
      <TextReg style={styles.comingSoonText}>Tickets coming soon!</TextReg>
    );
  }

  return (
    <Button
      size={size}
      variant={ButtonVariants.PURPLE}
      onPress={handleButtonPress}
      style={styles.button}
      loading={isPurchasing}
      disabled={isPurchasing}
      hideChildrenOnLoading={true}
    >
      <TextReg>Get Tickets</TextReg>
    </Button>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    button: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      marginBottom: isMobile ? 0 : 5,
    },
    comingSoonText: {
      fontSize: isMobile ? 12 : 14,
      opacity: 0.7,
    },
  });
